/**
 * "Seen" persistence.
 *
 * Tracks whether a user has already completed/dismissed a given tour, keyed by
 * tour id + version. Bumping a tour's `version` makes `hasSeen` return false again,
 * so a redesigned tour re-shows.
 *
 * The default implementation uses localStorage. It is deliberately behind the
 * `SeenStore` interface so a backend-synced adapter (per-user, cross-device) can be
 * dropped in later via `tourManager.init({ seenStore })` with zero changes to tours.
 */

export interface SeenStore {
  hasSeen(id: string, version: number): boolean;
  markSeen(id: string, version: number): void;
  reset(id: string): void;
}

const storageKey = (id: string): string => `asu.tour.${id}`;

interface SeenRecord {
  version: number;
  seenAt: string;
}

export const localStorageSeenStore: SeenStore = {
  hasSeen(id, version) {
    try {
      const raw = window.localStorage.getItem(storageKey(id));
      if (!raw) return false;
      const record = JSON.parse(raw) as SeenRecord;
      // Seen at this version or a newer one → considered seen.
      return typeof record.version === 'number' && record.version >= version;
    } catch {
      return false;
    }
  },

  markSeen(id, version) {
    try {
      const record: SeenRecord = { version, seenAt: new Date().toISOString() };
      window.localStorage.setItem(storageKey(id), JSON.stringify(record));
    } catch {
      // Storage unavailable (private mode / quota) — fail silently; worst case the
      // tour may show again next visit.
    }
  },

  reset(id) {
    try {
      window.localStorage.removeItem(storageKey(id));
    } catch {
      // no-op
    }
  }
};
